import PropTypes from 'prop-types';
import React from 'react';

/**
 * Shared channel reference — coloured dot + label chips. Shown once per screen
 * so the colour mapping (which is consistent across charts and the expandable
 * KPI breakdowns) is learnable.
 *
 * items: [{ id, label, color }]
 */
const ChannelLegend = ({
  items,
  className = '',
  hiddenIds = [],
  onItemClick,
  onItemMouseEnter,
  onItemMouseLeave,
}) => {
  if (!items || !items.length) return null;

  const isInteractive = Boolean(
    onItemClick || onItemMouseEnter || onItemMouseLeave,
  );

  return (
    <div className={`flex flex-wrap items-center gap-x-4 gap-y-2 ${className}`}>
      {items.map(item => {
        const isHidden = hiddenIds.includes(item.id);

        return (
          <span
            key={item.id}
            role={onItemClick ? 'button' : undefined}
            tabIndex={onItemClick ? 0 : undefined}
            onClick={onItemClick ? () => onItemClick(item) : undefined}
            onKeyDown={
              onItemClick
                ? e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onItemClick(item);
                    }
                  }
                : undefined
            }
            onMouseEnter={
              onItemMouseEnter ? () => onItemMouseEnter(item) : undefined
            }
            onMouseLeave={onItemMouseLeave}
            className={`flex items-center gap-2 text-xs text-grey-darker ${
              isInteractive ? 'cursor-pointer select-none' : ''
            } ${isHidden ? 'opacity-40 line-through' : ''}`}
          >
            <span
              className="inline-block rounded-full"
              style={{
                width: 10,
                height: 10,
                backgroundColor: item.color,
                opacity: isHidden ? 0.4 : 1,
              }}
            />
            {item.label}
          </span>
        );
      })}
    </div>
  );
};

ChannelLegend.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      label: PropTypes.string,
      color: PropTypes.string,
    }),
  ),
  className: PropTypes.string,
  hiddenIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  ),
  onItemClick: PropTypes.func,
  onItemMouseEnter: PropTypes.func,
  onItemMouseLeave: PropTypes.func,
};

export default ChannelLegend;
